import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button';
import { makeStyles } from '@material-ui/core/styles';
import { closeAddressDialog } from 'reducers/DialogsReducer';
import Dialog from 'components/Dialog';
import Paper from '@material-ui/core/Paper';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { handleError } from 'reducers/ErrorReducer';
import AddressHistory from './AddressHistory';
import AddressSOPs from './AddressSOPs';
import AddressBulletins from './AddressBulletins';
import AddressParties from './AddressParties';
import AddressZones from './AddressZones';
import { getAddressDetails } from 'reducers/AddressReducer';
import AddressEdit from './AddressEdit';
import AddressCoordinates from './AddressCoordinates';
import AddressPrePlan from './AddressPrePlan';
import Attachments from 'components/Attachments';
import AddressPlace from './AddressPlace';
import AddressPerson from './AddressPerson';
import { getAddressSops } from 'reducers/AddressReducer';
import { findBulletinsData } from 'reducers/BulletinReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';

const useStyles = makeStyles((theme) => ({
  content: {
    margin: -theme.spacing(3),
  },
  RedTabs: {
    color: 'red',
  },
}));

function Address(props) {
  const classes = useStyles();
  const { ptsAddressID, copyAddress } = props;
  const [tab, setTab] = useState('edit');
  const [address, setAddress] = useState(null);
  const mountedRef = useRef(true);
  const newAddress = Boolean(!ptsAddressID || copyAddress);
  const [isAddressFormDirty, setIsAddressFormDirty] = useState(false);
  const [countOfSops, setCountOfSops] = useState(0);
  const [countOfBulletin, setCountOfBulletin] = useState(0);
  const addressPerms = getFullPermissions('globals', 'Addresses', 'any');

  useEffect(() => {
    if (props.tab) setTab(props.tab);
    getAddressData();
    if (!newAddress) {
      getSOPAndBulletinNumber();
    }
    return () => {
      mountedRef.current = false;
    };
    // eslint-disable-next-line
  }, [props.tab]);
  const getSOPAndBulletinNumber = async () => {
    try {
      await Promise.all([getSOPs(), getBulletins()]);
    } catch (err) {
      props.handleError(err, 'Error loading SOPs or bulletins.');
    }
  };

  const getSOPs = async () => {
    try {
      const sops = await getAddressSops(ptsAddressID);
      if (!mountedRef.current) return;
      setCountOfSops(processSOPData(sops).length);
    } catch (err) {
      props.handleError(err, 'Error receiving event location data.');
    }
  };

  const getBulletins = async () => {
    try {
      const bulletins = await findBulletinsData({ ptsAddressID });
      if (!mountedRef.current) return;
      setCountOfBulletin(bulletins.length);
    } catch (err) {
      props.handleError(err, 'Error receiving address bulletins data.');
    }
  };

  const processSOPData = (data) => {
    return data.map((row) => {
      if (row.IsDeleted === false) return { ...row, id: row.ROWGUID };
    });
  };

  const handleSOPChange = (newCount) => {
    setCountOfSops(newCount);
  };

  const handleBulletinChange = (newCount) => {
    setCountOfBulletin(newCount);
  };

  const getAddressData = async () => {
    try {
      const data = await getAddressDetails(ptsAddressID);
      if (!mountedRef.current) return;
      data?.data.length && setAddress(data.data[0]);
    } catch (err) {
      props.handleError(err, 'Error receiving event location data.');
    }
  };
  const close = (data) => {
    if (isAddressFormDirty) {
      if (confirm('you have unsaved work. do you want to continue?')) {
        props.closeAddressDialog(data);
      }
    } else props.closeAddressDialog(data);
  };
  const renderActions = () => {
    return (
      <>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };
  const getTitle = () => {
    const title = newAddress ? 'Add Address' : 'Edit Address';
    return newAddress || addressPerms.Edit ? (
      `${title}`
    ) : (
      <div className="d-flex" style={{ width: '100%', justifyContent: 'left', textAlign: 'left' }}>
        <span style={{ flex: 0.5 }}>{}</span>
        <span style={{ flex: 0.5, color: 'red', fontWeight: 'bold', fontSize: '21px' }}>
          VIEW ONLY MODE
        </span>
      </div>
    );
  };
  return (
    <Dialog toolbar onClose={close} title={getTitle()} actions={renderActions()} fullScreen>
      <div className={classes.content}>
        <Paper square>
          <Tabs value={tab} onChange={(ev, val) => setTab(val)}>
            <Tab label="Edit Address" value="edit" />
            <Tab label="Attachments" value="attachments" disabled={newAddress} />
            <Tab label="Coordinates" value="coordinates" disabled={newAddress} />
            <Tab label="History" value="history" disabled={newAddress} />
            <Tab label="Parties" value="parties" disabled={newAddress} />
            <Tab label="Pre-Plan" value="pre-plan" disabled={newAddress} />
            <Tab
              label={countOfSops > 0 ? `SOPs (${countOfSops})` : 'SOPs'}
              value="sops"
              className={countOfSops > 0 ? classes.RedTabs : ''}
              disabled={newAddress}
            />
            <Tab label="Zones" value="zones" disabled={newAddress} />
            <Tab
              label={countOfBulletin > 0 ? `Bulletins (${countOfBulletin})` : 'Bulletins'}
              value="bulletins"
              className={countOfBulletin > 0 ? classes.RedTabs : ''}
              disabled={newAddress}
            />
            <Tab label="Places" value="places" disabled={newAddress} />
            <Tab label="Persons" value="persons" disabled={newAddress} />
          </Tabs>
        </Paper>
        {tab === 'edit' && (
          <AddressEdit
            ptsAddressID={ptsAddressID}
            copyAddress={copyAddress}
            setIsAddressFormDirty={setIsAddressFormDirty}
          />
        )}
        {!newAddress && (
          <>
            {tab === 'history' && <AddressHistory ptsAddressID={ptsAddressID} addressPerms={addressPerms} />}
            {tab === 'sops' && (
              <AddressSOPs ptsAddressID={ptsAddressID} onSOPChange={handleSOPChange} addressPerms={addressPerms} />
            )}
            {tab === 'parties' && <AddressParties ptsAddressID={ptsAddressID} addressPerms={addressPerms} />}
            {tab === 'zones' && <AddressZones ptsAddressID={ptsAddressID} address={address} addressPerms={addressPerms} />}
            {tab === 'attachments' && (
              <Attachments type="Address" id={ptsAddressID} permissions={addressPerms} />
            )}
            {tab === 'coordinates' && (
              <AddressCoordinates ptsAddressID={ptsAddressID} address={address} addressPerms={addressPerms}  />
            )}
            {tab === 'pre-plan' && <AddressPrePlan ptsAddressID={ptsAddressID}  addressPerms={addressPerms} />}
            {tab === 'bulletins' && (
              <AddressBulletins
                ptsAddressID={ptsAddressID}
                address={address}
                onBulletinChange={handleBulletinChange}
                addressPerms={addressPerms}
              />
            )}
            {tab === 'places' && <AddressPlace ptsAddressID={ptsAddressID} />}
            {tab === 'persons' && (
              <AddressPerson ptsAddressID={ptsAddressID} addressPerms={addressPerms} />
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

export default connect(null, {
  closeAddressDialog,
  handleError,
})(Address);
